import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect } from 'react';
import CustomButton from '../components/CustomButton';
import CustomCard from '../components/CustomCard';
import CustomTypography from '../components/CustomTypography';
import { triggerGTMEvent } from '../components/GTMEvent';

const HomePage = ({ router }) => {
    useEffect(() => {
        window.scrollTo(0, 0); // Moves the cursor to the top-left of the page
    }, []); // Empty dependency array ensures it runs only once when the component mounts

    const navigateTo = (path) => {
        const dashboardEvent = gaEventOfHomePage(path);
        triggerGTMEvent("Home", dashboardEvent);
        router.navigate(path); // This navigates to the specific page

    };

    const gaEventOfHomePage = (path) => {
        switch (path) {
            case '/prepayment-calculator':
                return 'HOME > Loan Prepayment';
            case '/emi-calculator':
                return 'HOME > Loan EMI';
            case '/lumpsuminvestment-calculator':
                return 'HOME > Lumpsum Investment';
            case '/sipinvestment-calculator':
                return 'HOME > SIP Investment';
            default:
                return 'HOME > Unknown Link';
        }
    }

    return (
        <Container maxWidth="lg" sx={{ padding: '20px' }}>
            <Box sx={{ textAlign: 'center', marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom>
                    FinCals
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Use our calculators to save money, grow investments, and plan a secure financial future.
                </Typography>
            </Box>

            {/* Why Choose Us Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h6" gutterBottom align="left">
                    Why Choose Us?
                </Typography>
                <Typography variant="body2" align="left" paragraph>
                    Our website provides simple yet powerful calculators that help you plan your finances with ease. Here's why we stand out:
                </Typography>
                <Grid container>
                    <Grid item size={{ xs: 1, sm: 1, md: 0.5 }}> <VerifiedIcon /></Grid>
                    <Grid item size={{ xs: 11, sm: 11, md: 11 }}> <CustomTypography variant="body2">Easy to use, no complex math involved.</CustomTypography></Grid>
                </Grid>
                <Grid container>
                    <Grid item size={{ xs: 1, sm: 1, md: 0.5 }}> <VerifiedIcon /></Grid>
                    <Grid item size={{ xs: 11, sm: 11, md: 11 }}>  <CustomTypography variant="body2">Accurate financial insights to guide your decisions.</CustomTypography></Grid>
                </Grid>
                <Grid container>
                    <Grid item size={{ xs: 1, sm: 1, md: 0.5 }}> <VerifiedIcon /></Grid>
                    <Grid item size={{ xs: 11, sm: 11, md: 11 }}>  <CustomTypography variant="body2">Completely free—no hidden fees, no sign-ups required!</CustomTypography></Grid>
                </Grid>
                <Grid container>
                    <Grid item size={{ xs: 1, sm: 1, md: 0.5 }}> <VerifiedIcon /></Grid>
                    <Grid item size={{ xs: 11, sm: 11, md: 11 }}>  <CustomTypography variant="body2">Your privacy matters—we don't collect any personal data.</CustomTypography></Grid>
                </Grid>
                <Grid container>
                    <Grid item size={{ xs: 1, sm: 1, md: 0.5 }}> <VerifiedIcon /></Grid>
                    <Grid item size={{ xs: 11, sm: 11, md: 11 }}> <CustomTypography variant="body2" fontWeight="bold" >Return to this page anytime and pick up right where you left off!</CustomTypography></Grid>
                </Grid>
            </Box>

            {/* Features Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h6" gutterBottom align="left">
                    Loan Calculators
                </Typography>
                <Grid container spacing={1}>
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                        <CustomCard
                            titleSize='h6'
                            title="Loan Prepayment Calculator"
                            description="Calculate interest savings by making extra payments on your loan. Plan prepayments efficiently with this tool."
                        >
                            <CustomButton variant="contained" color="primary" onClick={() => navigateTo('/prepayment-calculator')}>
                                Launch Calculator
                            </CustomButton>
                        </CustomCard>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                        <CustomCard
                            titleSize="h6"
                            title="EMI Calculator"
                            description="Easily calculate your monthly loan EMI and total interest payable over the loan tenure. Plan your finances with precision."
                        >
                            <CustomButton variant="contained" color="primary" onClick={() => navigateTo('/emi-calculator')}>
                                Launch Calculator
                            </CustomButton>
                        </CustomCard>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h6" gutterBottom align="left">
                    Investment Calculators
                </Typography>
                <Grid container spacing={1}>
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                        <CustomCard
                            titleSize='h6'
                            title="Lumpsum Calculator"
                            description="See how a lumpsum investment grows with annual returns. Use this tool to plan long-term investments wisely."
                        >
                            <CustomButton variant="contained" color="primary" onClick={() => navigateTo('/lumpsuminvestment-calculator')}>
                                Launch Calculator
                            </CustomButton>
                        </CustomCard>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                        <CustomCard
                            titleSize='h6'
                            title="SIP Calculator"
                            description="Understand how monthly SIP investments grow with compound interest. A great tool for financial planning."
                        >
                            <CustomButton variant="contained" color="primary" onClick={() => navigateTo('/sipinvestment-calculator')}>
                                Launch Calculator
                            </CustomButton>
                        </CustomCard>
                    </Grid>
                </Grid>
            </Box>

            {/* Call to Action */}
            <Box sx={{ textAlign: 'center' }}>
                <CustomTypography variant="h6" gutterBottom>
                    Start Planning Your Financial Future Today!
                </CustomTypography>
            </Box>
        </Container >
    );
};

export default HomePage;
